import axios from 'axios';
import { getDistinctId } from './analytics';
import { regionParam } from './regions';
import { supabase } from './supabase';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000',
  timeout: 30000, // 30 seconds timeout for search requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);

    // Handle different error types
    if (error.response?.status === 503) {
      throw new Error('Search service is temporarily unavailable. Please check if the database is running.');
    } else if (error.response?.status >= 500) {
      throw new Error('Internal server error. Please try again later.');
    } else if (error.response?.status === 400) {
      throw new Error(error.response.data.detail || 'Invalid request');
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('Request timeout. The search is taking too long.');
    } else if (error.code === 'ERR_NETWORK') {
      throw new Error('Network error. Please check your connection and ensure the backend is running.');
    }

    throw error;
  }
);

/**
 * Natural-language "smart search" preference (the conversational LLM
 * query-understanding layer). Stored client-side so a logged-in user can A/B it
 * from their Profile without any account round-trip. Defaults to ON — matches the
 * server default; the browser only sends `understand:false` to opt a request out.
 */
const NL_SEARCH_KEY = 'wtb_nl_search';
export const getNlSearchPref = () => {
  try {
    return localStorage.getItem(NL_SEARCH_KEY) !== 'off'; // default on
  } catch {
    return true;
  }
};
export const setNlSearchPref = (on) => {
  try {
    localStorage.setItem(NL_SEARCH_KEY, on ? 'on' : 'off');
  } catch {
    /* storage blocked (private mode) — pref just won't persist */
  }
};

/**
 * Search for movies using natural language query
 * @param {string} query - Natural language search query
 * @param {number} limit - Maximum number of results (default: 10)
 * @returns {Promise<Object>} Search results
 */
export const searchMovies = async (query, limit = 10, filters = null, region = null) => {
  try {
    const regionCode = regionParam(region); // null for Worldwide
    const clientId = getDistinctId();
    const nlOn = getNlSearchPref();
    const response = await api.post('/search', {
      query: query.trim(),
      limit: Math.min(limit, 50), // Cap at 50 results
      ...(clientId ? { client_id: clientId } : {}),
      ...(regionCode ? { region: regionCode } : {}),
      // Only send the flag when opting OUT — omitting it lets the server default win.
      ...(nlOn ? {} : { understand: false }),
      ...(filters
        ? {
            providers: filters.providers || [],
            year_start: filters.yearStart ?? null,
            year_end: filters.yearEnd ?? null,
            content_type: filters.contentType ?? null,
            filters_explicit: true,
          }
        : {}),
    });

    return response.data;
  } catch (error) {
    console.error('Search failed:', error);
    throw error;
  }
};

/** Authenticated, allowlisted preview of one controlled RAG arm. */
export const searchRagExperiment = async (query, architecture) => {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Sign in with the experiment account first.');
  const response = await api.post('/experiments/rag-search', {
    query: query.trim(), architecture,
  }, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 90000,
  });
  return response.data;
};

/**
 * Get movies similar to a specific movie
 * @param {number} movieId - TMDB movie ID
 * @param {number} limit - Maximum number of results (default: 5)
 * @returns {Promise<Array>} Similar movies
 */
export const getSimilarMovies = async (movieId, limit = 5) => {
  try {
    const response = await api.get(`/movies/${movieId}/similar`, {
      params: { limit: Math.min(limit, 20) }
    });

    return response.data;
  } catch (error) {
    console.error('Failed to get similar movies:', error);
    throw error;
  }
};

/**
 * Get relational "more like this" from the intelligence graph (GraphRAG). Ranks by
 * shared people / themes / genres, each with a `why` explanation. Fails soft to [].
 * @param {number} tmdbId
 * @param {number} [limit]
 */
export const getGraphSimilar = async (tmdbId, limit = 12) => {
  try {
    const response = await api.get(`/similar/${tmdbId}`, { params: { limit } });
    return response.data?.results || [];
  } catch (error) {
    console.error('Failed to get graph-similar:', error);
    return [];
  }
};

/** Public, source-aware intelligence for a typed TMDB title. */
export const getTitleIntelligence = async (mediaType, tmdbId, territory = 'IN') => {
  const type = mediaType === 'tv' ? 'series' : mediaType;
  const response = await api.get(
    `/v1/titles/${encodeURIComponent(type)}/${encodeURIComponent(tmdbId)}/intelligence`,
    { params: { territory, similar_limit: 8 }, timeout: 12000 }
  );
  return response.data;
};

/**
 * The dark-launch cohort of titles that HAVE a full intelligence view built
 * (the ones the gated `/v1/titles/.../intelligence` endpoint will serve). Used by
 * the Profile page to surface sample "behind the black box" links. Fails soft to [].
 * @returns {Promise<Array<{media_type, tmdb_id, title}>>}
 */
export const getIntelCohort = async () => {
  try {
    const response = await api.get('/v1/titles/dark-launch/cohort', { timeout: 8000 });
    return response.data?.titles || [];
  } catch (error) {
    console.error('getIntelCohort failed:', error);
    return [];
  }
};

export const submitBusinessInterest = async (payload) => {
  const response = await api.post('/v1/business-interest', payload, { timeout: 12000 });
  return response.data;
};

/**
 * Get structured AI Picks (tile-bound recommendations) for the AI Picks Rail.
 * Returns { picks: [{ id, why }], provider }. Fails soft to { picks: [] } so the
 * rail can just not render rather than break the page.
 * @param {string} query
 * @param {Array} results - the ranked results already shown (movies or manhua)
 * @param {Object} [refinementAnswers]
 * @param {number} [n] - how many picks (1-5)
 */
export const getAIPicks = async (query, results, refinementAnswers = null, n = 3) => {
  try {
    const response = await api.post('/curate/picks', {
      query: (query || '').trim(),
      results: (results || []).slice(0, 24),
      ...(refinementAnswers ? { refinement_answers: refinementAnswers } : {}),
      n,
    }, { timeout: 45000 });
    return response.data || { picks: [] };
  } catch (error) {
    console.error('getAIPicks failed:', error);
    return { picks: [] };
  }
};

/**
 * Browse a JustWatch-style rail (Top 10 / New / genre rows) with an optional
 * streamer filter. Non-semantic; the backend filters + sorts + caches.
 * Fails soft to [] so a single rail can quietly not render.
 * @param {string} rail - 'top' | 'new' | 'free' | a genre (e.g. 'horror')
 * @param {Object} [opts] - { provider, genre, type, limit }
 * @returns {Promise<Array>} list of MovieResult
 */
export const browse = async (rail = 'top', { provider, genre, type, limit = 20 } = {}) => {
  try {
    const response = await api.get('/browse', {
      params: {
        rail,
        ...(provider ? { provider } : {}),
        ...(genre ? { genre } : {}),
        ...(type ? { type } : {}),
        limit,
      },
    });
    return response.data?.results || [];
  } catch (error) {
    console.error(`browse(${rail}) failed:`, error);
    return [];
  }
};

/**
 * Streamer-filter chips: major brands that have content in the data.
 * @returns {Promise<{brands: Array<{key,label}>, providers: string[]}>}
 */
export const getStreamers = async () => {
  try {
    const response = await api.get('/browse/providers');
    return response.data || { brands: [], providers: [] };
  } catch (error) {
    console.error('getStreamers failed:', error);
    return { brands: [], providers: [] };
  }
};

/**
 * Search the manhua/manhwa/manga collection.
 * Same shape as searchMovies but hits the dedicated /manhua/search endpoint.
 */
export const searchManhua = async (query, limit = 20, filters = {}) => {
  try {
    const clientId = getDistinctId();
    const response = await api.post('/manhua/search', {
      query: query.trim(),
      limit: Math.min(limit, 50),
      ...(clientId ? { client_id: clientId } : {}),
      ...filters,  // optional: content_kind, status, min_year, content_ratings
    });
    return response.data;
  } catch (error) {
    console.error('Manhua search failed:', error);
    throw error;
  }
};

/**
 * Resolve a MangaDex cover URL for display.
 * - Served DIRECTLY from uploads.mangadex.org to the browser. We deliberately do
 *   NOT proxy through our own origin: MangaDex sits behind Cloudflare bot-management
 *   that blocks our datacenter (Hetzner) IP — the origin proxy returns 502, while a
 *   residential browser fetches covers fine (the same path the chapter-page images
 *   already use successfully). A foreign Referer is accepted by MangaDex.
 * - Request a downscaled thumbnail (.512.jpg ~130KB / .256.jpg ~36KB) instead of
 *   the multi-MB original PNG.
 * Non-mangadex / empty URLs are returned untouched.
 */
export const manhuaCover = (url, size = 512) => {
  if (!url || !url.startsWith('https://uploads.mangadex.org/')) return url;
  return size ? `${url}.${size}.jpg` : url;
};

/**
 * Fetch one manhua's full payload by MangaDex UUID. Backend does an O(1)
 * Qdrant retrieve since the UUID is the point ID.
 */
export const getManhuaById = async (id) => {
  try {
    const response = await api.get(`/manhua/${encodeURIComponent(id)}`);
    return response.data;
  } catch (err) {
    if (err.response?.status === 404) return null;
    console.error('getManhuaById failed:', err);
    throw err;
  }
};

/**
 * Cached chapter list for one manhua (English-only for now).
 */
export const getChapters = async (mangaId, { language = 'en', offset = 0, limit = 200, order = 'asc' } = {}) => {
  const response = await api.get(`/manhua/${mangaId}/chapters`, {
    params: { language, offset, limit, order },
    timeout: 60000,  // cold cache miss can take several seconds
  });
  return response.data;
};

/**
 * Live page URLs for one chapter — never cached server-side, never cached here.
 * The browser then loads `${base_url}/data/${hash}/${filename}` for each page.
 */
export const getChapterPages = async (chapterId, { dataSaver = false } = {}) => {
  const response = await api.get(`/manhua/chapters/${chapterId}/pages`, {
    params: { data_saver: dataSaver },
  });
  return response.data;
};

/**
 * Server-side proxy URL for one chapter page (by index). Used as a fallback when
 * the direct MangaDex@Home CDN image 404s — the backend re-routes via a fresh
 * at-home node and streams the bytes. Returns an absolute URL the <img> can use.
 */
export const chapterImageProxyUrl = (chapterId, pageIndex, { dataSaver = false } = {}) => {
  const base = api.defaults.baseURL || '';
  return `${base}/manhua/chapters/${chapterId}/img/${pageIndex}?data_saver=${dataSaver ? 'true' : 'false'}`;
};

/**
 * Manhua homepage rails (Popular, Latest Updates, genres, demographics).
 * Discovery view shown on /manhua when there's no active search.
 */
export const getManhuaBrowse = async () => {
  const response = await api.get('/manhua/browse', { timeout: 30000 });
  return response.data; // { rails: [{ key, title, items: [...] }] }
};

/**
 * Get manhua collection stats.
 */
export const getManhuaStats = async () => {
  try {
    const response = await api.get('/manhua/stats');
    return response.data;
  } catch (error) {
    console.error('Failed to get manhua stats:', error);
    throw error;
  }
};

/**
 * Best-effort country code (ISO 3166-1 alpha-2) for the region selector's default,
 * detected server-side from the request (Cloudflare CF-IPCountry / GeoIP). Returns
 * null when undetectable so the UI falls back to Worldwide. Short timeout — this
 * must never block first paint.
 * @returns {Promise<string|null>}
 */
export const getGeo = async () => {
  try {
    const response = await api.get('/geo', { timeout: 4000 });
    return response.data?.country || null;
  } catch (error) {
    return null;
  }
};

/**
 * Check API health status
 * @returns {Promise<Object>} Health status
 */
export const checkHealth = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    console.error('Health check failed:', error);
    throw error;
  }
};

/**
 * Get database statistics
 * @returns {Promise<Object>} Database stats
 */
export const getStats = async () => {
  try {
    const response = await api.get('/stats');
    return response.data;
  } catch (error) {
    console.error('Failed to get stats:', error);
    throw error;
  }
};

/**
 * Get TMDB poster URL
 * @param {string} posterPath - TMDB poster path
 * @param {string} size - Image size (w154, w342, w500, w780, original)
 * @returns {string} Full poster URL
 */
export const getPosterUrl = (posterPath, size = 'w342') => {
  if (!posterPath) return null;
  return `https://image.tmdb.org/t/p/${size}${posterPath}`;
};

/**
 * Format movie release date
 * @param {string} releaseDate - ISO date string
 * @returns {string} Formatted date
 */
export const formatReleaseDate = (releaseDate) => {
  if (!releaseDate) return 'Unknown';

  try {
    const date = new Date(releaseDate);
    return date.getFullYear().toString();
  } catch (error) {
    return 'Unknown';
  }
};

/**
 * Format similarity score as percentage
 * @param {number} score - Similarity score (0-1)
 * @returns {string} Formatted percentage
 */
export const formatSimilarityScore = (score) => {
  if (typeof score !== 'number') return '0%';
  return `${Math.round(score * 100)}%`;
};

export default api; 
